'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import type { DeliveryMethodType, ProductApiResponse, TailoredApiData, VariantKey } from '@libs/common-types';
import { findSkuForVariantChange, isProductAvailableForAutoship, isVariantInStock, mapVariantGroups, type VariantGroup, type VariantGroupOption } from '../../product-details';
import type { MiniPdpForm } from '../types';
type MiniPdpBaseProduct = ProductApiResponse['data'];
export type MiniPdpVariant = VariantGroupOption;
export type MiniPdpVariantGroup = VariantGroup;
export type MiniPdpVariantsProps = Readonly<{
  base: MiniPdpBaseProduct | null;
  tailored: TailoredApiData | null;
}>;
export const useMiniPdpVariants = t0 => {
  const $ = _c(25);
  const {
    base,
    tailored
  } = t0;
  const {
    getValues,
    setValue
  } = useFormContext();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      name: "selectedSku"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const selectedSku = useWatch(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      name: "fulfilmentMethod"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const fulfilmentMethod = useWatch(t2);
  const activeSku = base?.variants[selectedSku] ? selectedSku : base?.defaultSku;
  const activeProduct = activeSku && base ? base.variants[activeSku] : undefined;
  let t3;
  if ($[2] !== activeProduct?.variantValues) {
    t3 = activeProduct?.variantValues ?? {};
    $[2] = activeProduct?.variantValues;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const activeVariants = t3;
  let t4;
  if ($[4] !== fulfilmentMethod) {
    t4 = fulfilmentMethod.toUpperCase();
    $[4] = fulfilmentMethod;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const method = t4 as DeliveryMethodType;
  let t5;
  if ($[6] !== activeVariants || $[7] !== base || $[8] !== getValues || $[9] !== method || $[10] !== setValue || $[11] !== tailored) {
    t5 = (group, value) => {
      if (!base?.variantToSkuMap) {
        return;
      }
      const sku = findSkuForVariantChange({
        activeVariants,
        group,
        isSkuAvailable: candidateSku => isVariantInStock({
          sku: candidateSku,
          product: base.variants[candidateSku],
          method,
          tailored: tailored ?? undefined
        }),
        value,
        variantToSkuMap: base.variantToSkuMap
      });
      if (!sku) {
        return;
      }
      setValue("selectedSku", sku, {
        shouldDirty: true,
        shouldValidate: true
      });
      const nextProduct = base.variants[sku];
      const isAutoshipUnavailable = !nextProduct || !isProductAvailableForAutoship(nextProduct);
      if (getValues("purchaseOption") === "autoship" && isAutoshipUnavailable) {
        setValue("purchaseOption", "one-time");
      }
    };
    $[6] = activeVariants;
    $[7] = base;
    $[8] = getValues;
    $[9] = method;
    $[10] = setValue;
    $[11] = tailored;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const handleVariantChange = t5;
  const t6 = base?.uniqueVariantValues;
  const t7 = base?.variantToSkuMap;
  let t8;
  if ($[13] !== activeVariants || $[14] !== base?.variants || $[15] !== method || $[16] !== t6 || $[17] !== t7 || $[18] !== tailored) {
    t8 = mapVariantGroups({
      activeVariants,
      method,
      tailored,
      uniqueVariantValues: t6,
      variantToSkuMap: t7,
      variants: base?.variants ?? {}
    });
    $[13] = activeVariants;
    $[14] = base?.variants;
    $[15] = method;
    $[16] = t6;
    $[17] = t7;
    $[18] = tailored;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const variantGroups = t8;
  let t9;
  if ($[20] !== activeProduct || $[21] !== activeSku || $[22] !== handleVariantChange || $[23] !== variantGroups) {
    t9 = {
      activeProduct,
      activeSku,
      handleVariantChange,
      variantGroups
    };
    $[20] = activeProduct;
    $[21] = activeSku;
    $[22] = handleVariantChange;
    $[23] = variantGroups;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
};